import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LocationDetectionService } from '../services/locationDetectionService';

export type Language = 'en' | 'de' | 'zh' | 'fr';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    isLanguageDetected: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
    const [language, setLanguage] = useState<Language>('en');
    const [isLanguageDetected, setIsLanguageDetected] = useState<boolean>(false);

    // Check if user has manually selected a language before
    const getStoredLanguage = (): Language | null => {
        try {
            const stored = localStorage.getItem('niveau-eleve-language');
            if (stored && ['en', 'de', 'fr', 'zh'].includes(stored)) {
                return stored as Language;
            }
        } catch (error) {
            console.warn('Failed to read stored language:', error);
        }
        return null;
    };

    // Store user's manual language selection
    const storeLanguage = (lang: Language): void => {
        try {
            localStorage.setItem('niveau-eleve-language', lang);
            localStorage.setItem('niveau-eleve-language-manual', 'true');
        } catch (error) {
            console.warn('Failed to store language:', error);
        }
    };

    // Check if user has manually selected a language
    const isManualLanguageSelection = (): boolean => {
        try {
            return localStorage.getItem('niveau-eleve-language-manual') === 'true';
        } catch (error) {
            return false;
        }
    };

    // Auto-detect language on component mount
    useEffect(() => {
        const initializeLanguage = async () => {
            // If user has manually selected a language before, use that
            const storedLanguage = getStoredLanguage();
            if (storedLanguage && isManualLanguageSelection()) {
                setLanguage(storedLanguage);
                setIsLanguageDetected(true);
                return;
            }

            // Otherwise, auto-detect based on location
            try {
                const detectedLanguage = await LocationDetectionService.detectUserLanguage();
                setLanguage(detectedLanguage);
                setIsLanguageDetected(true);
            } catch (error) {
                console.warn('Language detection failed, using default:', error);
                setLanguage('en');
                setIsLanguageDetected(true);
            }
        };

        initializeLanguage();
    }, []);

    // Enhanced setLanguage function that stores manual selections
    const handleSetLanguage = (lang: Language): void => {
        setLanguage(lang);
        storeLanguage(lang);
    };

    return (
        <LanguageContext.Provider value={{ 
            language, 
            setLanguage: handleSetLanguage, 
            isLanguageDetected 
        }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
} 